import { FC, ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';

import { AddDocumentIcon } from '../../../assets/icons/index';
import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import { TableRow } from '../../../components/table/index';
import { Tooltip } from '../../../components/tooltip/index';
import { useFormat } from '../../../hooks/index';
import { selectedCompanyStorage } from '../../../storage/company/index';
import { downloadDocumentStorage } from '../../../storage/modals/index';
import { formatDisplayValue } from '../../../utils/format-display-value.util';
import { getS3FileOriginalName } from '../../../utils/get-s3file-original-name.util';
import { InvestmentHistory } from '../hooks/index';
import {
  Column,
  columnOrderMap,
  columnTranslationKey,
  investmentTypeTranslationKey,
} from './investment-ledger.types';

export type TableItemProps = { data: InvestmentHistory; mode?: 'TABLE' };

export const InvestmentItem: FC<TableItemProps> = ({
  data: {
    documents,
    id,
    investmentAmount,
    investmentDate,
    ownership,
    shareClassName,
    sharePrice,
    sharesCount,
    type,
    valuationName,
    postMoneyValuation,
  },
  mode,
}) => {
  const open = downloadDocumentStorage(({ open }) => open);
  const company = selectedCompanyStorage(({ company }) => company);
  const { format } = useFormat();

  const currency = company?.currency?.symbol || company?.currency?.iso3.toUpperCase();
  const payload: { id: string } & Record<Column, number | ReactNode | string> = {
    [Column.EVENT]: (
      <Tooltip content={valuationName}>
        <span className="max-w-[150px] truncate">{valuationName}</span>
      </Tooltip>
    ),
    [Column.INVESTMENT_DATE]: format(investmentDate, 'dd/MM/yyyy'),
    [Column.INVESTMENT_TYPE]: <AppFormattedMessage id={investmentTypeTranslationKey[type]} />,
    [Column.INVESTMENT_AMOUNT]: formatDisplayValue(investmentAmount, { prefix: currency }),
    [Column.SHARE_PRICE]: formatDisplayValue(sharePrice, { prefix: currency }),
    [Column.NO_OF_SHARES]: formatDisplayValue(sharesCount),
    [Column.OWNERSHIP_PERCENTAGE]: formatDisplayValue(ownership, { multiply: 100 }),
    [Column.AMOUNT_DUE]: '-',
    [Column.SHARE_CLASS]: (
      <Tooltip content={shareClassName}>
        <span className="max-w-[150px] truncate">{shareClassName}</span>
      </Tooltip>
    ),
    [Column.POST_MONEY_VALUATION]: formatDisplayValue(postMoneyValuation, { prefix: currency }),
    [Column.DOCUMENTS]: (
      <Tooltip
        content={
          documents.length === 1
            ? getS3FileOriginalName(documents[0]) || 'No files Attached'
            : `${documents.length} files attached to event`
        }
      >
        <AddDocumentIcon
          className="size-6 cursor-pointer p-[2px]"
          onClick={() => documents.length && open(documents)}
        />
      </Tooltip>
    ),
    id,
  };

  if (mode === 'TABLE')
    return (
      <TableRow
        columnOrder={columnOrderMap}
        fields={payload}
        labelClassName="text-xs font-[450] text-gray-700"
      />
    );

  return (
    <div className="flex w-full flex-col divide-y divide-gray-100 rounded-md bg-white shadow-sm">
      {columnOrderMap.map((column) => (
        <div
          className={twMerge(
            'flex items-center justify-between gap-2 text-nowrap px-4 py-[10px] text-sm font-[450] text-gray-700',
          )}
          key={column}
        >
          <AppFormattedMessage id={columnTranslationKey[column]} />
          <span>{payload[column]}</span>
        </div>
      ))}
    </div>
  );
};
